import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminCampaignCarryPromotionAPI } from '../services/adminApi';

const selectStyle = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  borderRadius: 8,
  border: '1px solid #d1d5db',
  fontSize: '0.875rem',
  background: '#fff',
};

const btnPrimary = {
  padding: '0.55rem 1.1rem',
  borderRadius: 8,
  border: 'none',
  background: '#dc2626',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: '0.875rem',
};

const btnSecondary = {
  ...btnPrimary,
  background: '#374151',
};

/**
 * Admin: promote #1 and #2 teams from a completed campaign's last season into the next campaign
 * (same team continuing). No relegation. Separate from Team Placement.
 */
const AdminCampaignCarryPromotion = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [sourceCampaigns, setSourceCampaigns] = useState([]);
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [targetSeasons, setTargetSeasons] = useState([]);

  const [sourceCampaignId, setSourceCampaignId] = useState('');
  const [targetCampaignId, setTargetCampaignId] = useState('');
  const [targetSeasonId, setTargetSeasonId] = useState('');

  const [preview, setPreview] = useState(null);
  /** row edits: { [teamId]: { selected, divisionId, action, force } } */
  const [rowState, setRowState] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [src, tgt] = await Promise.all([
          adminCampaignCarryPromotionAPI.getSourceCampaigns(),
          adminCampaignCarryPromotionAPI.getTargetCampaigns(),
        ]);
        setSourceCampaigns(src?.data || []);
        setAllCampaigns(tgt?.data || []);
      } catch (err) {
        console.error(err);
        setError(err.response?.data?.message || err.message || 'Failed to load seasons');
      }
    })();
  }, []);

  useEffect(() => {
    setTargetSeasonId('');
    setTargetSeasons([]);
    setPreview(null);
    setRowState({});
    if (!targetCampaignId) return;
    (async () => {
      try {
        const res = await adminCampaignCarryPromotionAPI.getCampaignSeasons(targetCampaignId);
        const list = res?.data || [];
        setTargetSeasons(list);
        const sorted = [...list].sort((a, b) =>
          String(a.start_date || '').localeCompare(String(b.start_date || ''))
        );
        if (sorted[0]?.id) setTargetSeasonId(String(sorted[0].id));
      } catch (err) {
        console.error(err);
        setError(err.response?.data?.message || 'Failed to load target season phases');
      }
    })();
  }, [targetCampaignId]);

  const loadPreview = useCallback(async () => {
    if (!sourceCampaignId || !targetCampaignId || !targetSeasonId) {
      setError('Select source season, target season, and target phase');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await adminCampaignCarryPromotionAPI.preview({
        sourceCampaignId,
        targetCampaignId,
        targetSeasonId,
      });
      const data = res?.data;
      setPreview(data);
      const next = {};
      (data?.candidates || []).forEach((c) => {
        next[c.teamId] = {
          selected: !!c.includeInApply && !c.alreadyApplied,
          divisionId: c.suggestedDivisionId || '',
          action: c.suggestedAction || 'manual',
          force: false,
        };
      });
      setRowState(next);
      setSuccess(
        `Preview loaded: ${(data?.candidates || []).length} top-two team(s). Review and apply continuing rows.`
      );
    } catch (err) {
      console.error(err);
      setPreview(null);
      setError(err.response?.data?.message || err.message || 'Preview failed');
    } finally {
      setLoading(false);
    }
  }, [sourceCampaignId, targetCampaignId, targetSeasonId]);

  const divisionsForSelect = useMemo(() => {
    const list = preview?.divisions || [];
    return list.filter((d) => !d.is_group_division);
  }, [preview]);

  const updateRow = (teamId, patch) => {
    setRowState((prev) => ({
      ...prev,
      [teamId]: { ...prev[teamId], ...patch },
    }));
  };

  const handleApply = async () => {
    if (!preview) return;
    const placements = [];
    (preview.candidates || []).forEach((c) => {
      const st = rowState[c.teamId];
      if (!c.isContinuing || !st?.selected || !st.divisionId) return;
      placements.push({
        teamId: c.teamId,
        newDivisionId: st.divisionId,
        action: st.action || 'manual',
      });
    });

    if (!placements.length) {
      setError(
        'No eligible teams selected. Teams must register and pay for the new season before promotion can be applied.'
      );
      return;
    }

    const confirmed = window.confirm(
      `Apply season carry promotion for ${placements.length} team(s)?\n\n` +
        `This updates division assignment on the target phase and sets each member's ` +
        `profile + roster skill to the new division minimum (team average updates too).\n` +
        `Already carried teams are skipped. No relegation.`
    );
    if (!confirmed) return;

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await adminCampaignCarryPromotionAPI.apply({
        sourceCampaignId,
        targetCampaignId,
        targetSeasonId,
        placements,
      });
      const applied = res?.data?.applied || [];
      const ok = applied.filter((a) => !a.skipped).length;
      const alreadyApplied = applied.filter((a) => a.reason === 'already_applied').length;
      const alreadyUpper = applied.filter((a) => a.reason === 'already_at_or_above').length;
      const skippedOther = applied.filter(
        (a) => a.skipped && a.reason !== 'already_applied' && a.reason !== 'already_at_or_above'
      ).length;
      const playersUpdated = (res?.data?.alignmentSummary?.playersUpdated || 0);
      setSuccess(
        `Applied ${ok} placement(s)` +
          (playersUpdated ? `; updated ${playersUpdated} player skill(s) to division min` : '') +
          (alreadyApplied ? `; ${alreadyApplied} already carried (skipped)` : '') +
          (alreadyUpper ? `; ${alreadyUpper} already in upper division` : '') +
          (skippedOther ? `; ${skippedOther} other skipped` : '')
      );
      await loadPreview();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || err.message || 'Apply failed');
    } finally {
      setLoading(false);
    }
  };

  const targetCampaignOptions = useMemo(() => {
    return (allCampaigns || []).filter((c) => String(c.id) !== String(sourceCampaignId));
  }, [allCampaigns, sourceCampaignId]);

  return (
    <div style={{ padding: '1.25rem', maxWidth: 1200 }}>
      <h2 style={{ margin: '0 0 0.35rem', fontSize: '1.35rem', color: '#111827' }}>
        Season promotion
      </h2>
      <p style={{ margin: '0 0 1.25rem', color: '#6b7280', fontSize: '0.9rem', maxWidth: 720 }}>
        After a completed season, promote teams that finished <strong>#1 or #2</strong> in their division
        and have <strong>registered and paid</strong> to continue the same team into a new season —
        one skill band higher. Elite / top band stays with an explicit message. No relegation.
        Teams that have not continued yet appear in preview but cannot be applied until they pay.
        On apply, each member&apos;s profile and roster skill are set to the <strong>new division minimum</strong>,
        and the team average is updated. Already carried teams are skipped on later runs.
        Source-phase division history is never overwritten. Separate from Team Placement.
      </p>

      {error ? (
        <div
          style={{
            marginBottom: '1rem',
            padding: '0.75rem 1rem',
            background: '#fef2f2',
            color: '#991b1b',
            borderRadius: 8,
            border: '1px solid #fecaca',
          }}
        >
          {error}
        </div>
      ) : null}
      {success ? (
        <div
          style={{
            marginBottom: '1rem',
            padding: '0.75rem 1rem',
            background: '#ecfdf5',
            color: '#065f46',
            borderRadius: 8,
            border: '1px solid #a7f3d0',
          }}
        >
          {success}
        </div>
      ) : null}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '1rem',
          marginBottom: '1rem',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.8rem', color: '#374151' }}>
          Source season (completed last phase)
          <select
            style={selectStyle}
            value={sourceCampaignId}
            onChange={(e) => {
              setSourceCampaignId(e.target.value);
              setPreview(null);
              setRowState({});
            }}
          >
            <option value="">Select…</option>
            {sourceCampaigns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title} — {c.last_season_name} ({c.last_season_status})
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.8rem', color: '#374151' }}>
          Target season
          <select
            style={selectStyle}
            value={targetCampaignId}
            onChange={(e) => setTargetCampaignId(e.target.value)}
          >
            <option value="">Select…</option>
            {targetCampaignOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title || c.name} ({c.status})
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.8rem', color: '#374151' }}>
          Target phase
          <select
            style={selectStyle}
            value={targetSeasonId}
            onChange={(e) => {
              setTargetSeasonId(e.target.value);
              setPreview(null);
              setRowState({});
            }}
            disabled={!targetCampaignId}
          >
            <option value="">Select…</option>
            {targetSeasons.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.status})
              </option>
            ))}
          </select>
        </label>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
        <button type="button" style={btnSecondary} disabled={loading} onClick={loadPreview}>
          {loading ? 'Loading…' : 'Preview / autofill'}
        </button>
        <button
          type="button"
          style={btnPrimary}
          disabled={loading || !preview}
          onClick={handleApply}
        >
          Apply selected
        </button>
      </div>
      <p style={{ margin: '0 0 1.25rem', fontSize: '0.8rem', color: '#6b7280', maxWidth: 720 }}>
        Apply sets each member&apos;s profile and roster skill to the new division minimum and updates
        the team average. Already carried teams are skipped on later runs.
      </p>

      {preview ? (
        <>
          <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.75rem' }}>
            Source phase: <strong>{preview.sourceSeason?.name}</strong>
            {' · '}
            Target: <strong>{preview.targetSeason?.name}</strong>
            {' · '}
            {(preview.candidates || []).filter((c) => c.isContinuing).length} continuing of{' '}
            {(preview.candidates || []).length} top-two teams
          </div>

          <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                  <th style={{ padding: '0.65rem 0.75rem' }}>Apply</th>
                  <th style={{ padding: '0.65rem 0.75rem' }}>Team</th>
                  <th style={{ padding: '0.65rem 0.75rem' }}>Canton</th>
                  <th style={{ padding: '0.65rem 0.75rem' }}>Played in (source phase)</th>
                  <th style={{ padding: '0.65rem 0.75rem' }}>#</th>
                  <th style={{ padding: '0.65rem 0.75rem' }}>Continuing</th>
                  <th style={{ padding: '0.65rem 0.75rem' }}>Current (target phase)</th>
                  <th style={{ padding: '0.65rem 0.75rem' }}>Carry → target</th>
                  <th style={{ padding: '0.65rem 0.75rem' }}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {(preview.candidates || []).map((c) => {
                  const st = rowState[c.teamId] || {};
                  const canApply =
                    !!c.isContinuing &&
                    !c.forfeit &&
                    !c.alreadyApplied &&
                    c.suggestedAction !== 'already_at_or_above';
                  const elite =
                    c.suggestedAction === 'stay_no_higher' ||
                    (c.message && /no higher/i.test(c.message));
                  const alreadyUpper = c.suggestedAction === 'already_at_or_above';
                  return (
                    <tr
                      key={c.teamId}
                      style={{
                        borderTop: '1px solid #e5e7eb',
                        opacity: canApply || c.alreadyApplied ? 1 : 0.65,
                      }}
                    >
                      <td style={{ padding: '0.55rem 0.75rem' }}>
                        <input
                          type="checkbox"
                          checked={!!st.selected && canApply}
                          disabled={!canApply}
                          title={
                            canApply
                              ? 'Include in apply'
                              : c.alreadyApplied
                                ? 'Already applied'
                                : 'Register and pay for the new season first'
                          }
                          onChange={(e) =>
                            updateRow(c.teamId, {
                              selected: e.target.checked,
                            })
                          }
                        />
                      </td>
                      <td style={{ padding: '0.55rem 0.75rem', fontWeight: 600 }}>{c.teamName}</td>
                      <td style={{ padding: '0.55rem 0.75rem' }}>{c.canton || '—'}</td>
                      <td style={{ padding: '0.55rem 0.75rem' }}>
                        <div>{c.oldDivisionName || '—'}</div>
                        {c.playedBandDivisionName &&
                        c.playedBandDivisionName !== c.oldDivisionName ? (
                          <div style={{ color: '#6b7280', fontSize: '0.75rem' }}>
                            Band: {c.playedBandDivisionName}
                            {c.oldSkillLevelMin != null ? ` (min ${c.oldSkillLevelMin})` : ''}
                          </div>
                        ) : null}
                      </td>
                      <td style={{ padding: '0.55rem 0.75rem' }}>{c.position}</td>
                      <td style={{ padding: '0.55rem 0.75rem' }}>
                        {c.isContinuing ? (
                          <span style={{ color: '#059669' }}>Yes</span>
                        ) : (
                          <span style={{ color: '#b45309' }}>Not yet — pay first</span>
                        )}
                      </td>
                      <td style={{ padding: '0.55rem 0.75rem' }}>
                        {c.currentTargetDivisionName ? (
                          <>
                            {c.currentTargetDivisionName}
                            {c.currentTargetSkillMin != null ? (
                              <span style={{ color: '#6b7280' }}> (min {c.currentTargetSkillMin})</span>
                            ) : null}
                          </>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>Not placed yet</span>
                        )}
                      </td>
                      <td style={{ padding: '0.55rem 0.75rem', minWidth: 200 }}>
                        {alreadyUpper ? (
                          <div style={{ marginBottom: 4, color: '#059669', fontSize: '0.8rem' }}>
                            Already in upper division — carry skipped
                          </div>
                        ) : null}
                        {elite ? (
                          <div style={{ marginBottom: 4, color: '#6b7280', fontSize: '0.8rem' }}>
                            No higher division (Elite / top band)
                          </div>
                        ) : null}
                        <select
                          style={selectStyle}
                          value={st.divisionId || ''}
                          disabled={!canApply}
                          onChange={(e) => {
                            const id = e.target.value;
                            const div = divisionsForSelect.find((d) => String(d.id) === String(id));
                            let action = 'manual';
                            if (
                              c.oldSkillLevelMin != null &&
                              div?.skill_level_min != null
                            ) {
                              if (Number(div.skill_level_min) > Number(c.oldSkillLevelMin)) {
                                action = 'promote';
                              } else if (
                                Number(div.skill_level_min) === Number(c.oldSkillLevelMin)
                              ) {
                                action = 'stay_no_higher';
                              }
                            }
                            updateRow(c.teamId, { divisionId: id, action });
                          }}
                        >
                          <option value="">—</option>
                          {divisionsForSelect
                            .filter(
                              (d) =>
                                !c.canton ||
                                !d.canton ||
                                String(d.canton).toLowerCase().includes(
                                  String(c.canton).toLowerCase()
                                ) ||
                                String(c.canton)
                                  .toLowerCase()
                                  .includes(String(d.canton).toLowerCase())
                            )
                            .map((d) => (
                              <option key={d.id} value={d.id}>
                                {d.name}
                                {d.skill_level_min != null ? ` (min ${d.skill_level_min})` : ''}
                              </option>
                            ))}
                        </select>
                      </td>
                      <td style={{ padding: '0.55rem 0.75rem', color: '#6b7280', maxWidth: 280 }}>
                        {c.notes || '—'}
                        {c.alreadyApplied ? (
                          <div style={{ color: '#059669', marginTop: 4 }}>Already applied</div>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default AdminCampaignCarryPromotion;
